import { BaseAPI } from 'API/BaseApi';
import { eAPITags } from 'API/eAPITags';
import { IUpdateOrderPatchRequest } from '../../ui-toolkit/orders/interfaces/IUpdateOrderPatchRequest';
import {
  ITimberOrderResponse,
  ITimberRecentOrderResponse,
} from '../../ui-toolkit/orders/interfaces/ITimberOrderResponse';
import { UUID } from '../../types/shared/UUID';
import QueryString from 'qs';
import { eUnderwriter } from '../../ui-toolkit/enums/eUnderwriter';
import { IServiceOrderResponse } from '../../ui-toolkit/orders/interfaces/IServiceOrderResponse';
import { ICreateOrderRequest } from '../../ui-toolkit/orders/interfaces/createOrder/ICreateOrderRequest';
import { IOrderDetailsData } from '../../ui-toolkit/orders/interfaces/IOrderDetailsData';
import { convertOrderDetailsFromAPIToData } from '../../ui-toolkit/orders/methods/convertApiToData';
import {
  convertFromSearchOrderResultRequestToSearchOrderResultData,
  ISearchOrderResultData,
  ISearchOrderResultRequest,
} from '../../ui-toolkit/orders/searchOrder/types/ISearchOrdersResult';
import {
  convertFromSearchOrdersSpecificationRequestToParameters,
  ISearchOrdersSpecificationData,
} from '../../ui-toolkit/orders/searchOrder/types/ISearchOrdersSpecification';

export const OrderApi = BaseAPI.injectEndpoints({
  endpoints: (build) => ({
    getOrder: build.query<IOrderDetailsData, UUID>({
      query: (orderId) => `/timber/api/internal/orders/${orderId}`,
      transformResponse: (response: IServiceOrderResponse) => convertOrderDetailsFromAPIToData(response),
      providesTags: (result, error, id) => [{ type: eAPITags.OrderData, id }],
    }),
    searchOrders: build.query<ISearchOrderResultData, ISearchOrdersSpecificationData>({
      query: (parameters) =>
        `/timber/api/internal/orders?${QueryString.stringify(
          convertFromSearchOrdersSpecificationRequestToParameters(parameters),
          {
            arrayFormat: 'repeat',
          }
        )}`,
      transformResponse: (raw: ISearchOrderResultRequest) =>
        convertFromSearchOrderResultRequestToSearchOrderResultData(raw),
      providesTags: (res, error, query) => [
        {
          type: eAPITags.OrderData,
          id: QueryString.stringify(convertFromSearchOrdersSpecificationRequestToParameters(query), {
            arrayFormat: 'repeat',
          }),
        },
      ],
    }),
    updateUnderwriter: build.mutation<void, { id: string; underwriter: eUnderwriter }>({
      query: ({ id, underwriter }) => ({
        url: `/orders/v5/admin/${id}/underwriter`,
        method: 'PUT',
        body: { underwriter },
      }),
      invalidatesTags: (res, err, { id }) => [{ type: eAPITags.OrderData, id }],
    }),
    updateOrderTimber: build.mutation<
      ITimberOrderResponse,
      { orderId: string; data: Partial<IUpdateOrderPatchRequest>; skipAddressValidation: boolean }
    >({
      query: ({ orderId, data, skipAddressValidation }) => ({
        url: `/timber/api/internal/orders/${orderId}?skipAddressValidation=${skipAddressValidation}`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (response, error, request) => [
        { type: eAPITags.MobilityGroupData, id: response?.clientCompany?.id || '' },
        { type: eAPITags.SectionData, id: response?.id },
        { type: eAPITags.OrderData, id: response?.id },
        { type: eAPITags.OrderDiscounts, id: response?.id },
        { type: eAPITags.OrdersByFilterParams, id: response?.id },
        { type: eAPITags.OrdersByFilterParams, id: 'LIST' },
        { type: eAPITags.TasksByFilterParams, id: 'LIST' },
        { type: eAPITags.SigningAppointmentListData, id: 'LIST' },
      ],
    }),
    listRecentOrders: build.query<ITimberRecentOrderResponse[] | [], void>({
      query: () => '/timber/api/internal/orders/recent',
      providesTags: (res, err, query) => [{ type: eAPITags.RecentOrders, id: JSON.stringify(query) }],
    }),
    createOrder: build.mutation<ITimberOrderResponse, ICreateOrderRequest>({
      query: (data: ICreateOrderRequest) => ({
        url: '/timber/api/internal/orders',
        method: 'POST',
        body: data,
      }),
    }),
  }),
});

export const {
  useGetOrderQuery,
  useSearchOrdersQuery,
  useUpdateUnderwriterMutation,
  useUpdateOrderTimberMutation,
  useListRecentOrdersQuery,
  useCreateOrderMutation,
  usePrefetch,
} = OrderApi;
